/**
 * Lightweight retail observability context for Sentry boundary tags (GAP-9).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CustomerBoundaryContext } from './customerBoundarySentry.js';

export type CustomerAuthProviderTag = 'google' | 'apple' | 'otp' | 'password';

interface CustomerObservabilityState {
  readonly lastAuthProvider?: CustomerAuthProviderTag;
  readonly salesPointCapabilityVersion?: string;
  readonly activeFlagsSnapshot?: string;
}

interface CustomerObservabilityContextValue extends CustomerObservabilityState {
  readonly setLastAuthProvider: (provider: CustomerAuthProviderTag) => void;
  readonly setSalesPointCapabilityVersion: (version: string | undefined) => void;
  readonly setActiveFlagsSnapshot: (snapshot: string | undefined) => void;
}

const CustomerObservabilityContext = createContext<CustomerObservabilityContextValue | null>(
  null,
);

let observabilitySnapshot: CustomerBoundaryContext = {};

export function getCustomerObservabilitySnapshot(): CustomerBoundaryContext {
  return observabilitySnapshot;
}

export function CustomerObservabilityProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<CustomerObservabilityState>({});

  useEffect(() => {
    observabilitySnapshot = {
      authProvider: state.lastAuthProvider,
      salesPointCapabilityVersion: state.salesPointCapabilityVersion,
      activeFlagsSnapshot: state.activeFlagsSnapshot,
    };
  }, [state.activeFlagsSnapshot, state.lastAuthProvider, state.salesPointCapabilityVersion]);

  const setLastAuthProvider = useCallback((provider: CustomerAuthProviderTag): void => {
    setState((prev) =>
      prev.lastAuthProvider === provider ? prev : { ...prev, lastAuthProvider: provider },
    );
  }, []);

  const setSalesPointCapabilityVersion = useCallback((version: string | undefined): void => {
    setState((prev) =>
      prev.salesPointCapabilityVersion === version
        ? prev
        : { ...prev, salesPointCapabilityVersion: version },
    );
  }, []);

  const setActiveFlagsSnapshot = useCallback((snapshot: string | undefined): void => {
    setState((prev) =>
      prev.activeFlagsSnapshot === snapshot ? prev : { ...prev, activeFlagsSnapshot: snapshot },
    );
  }, []);

  const value = useMemo(
    (): CustomerObservabilityContextValue => ({
      ...state,
      setLastAuthProvider,
      setSalesPointCapabilityVersion,
      setActiveFlagsSnapshot,
    }),
    [setActiveFlagsSnapshot, setLastAuthProvider, setSalesPointCapabilityVersion, state],
  );

  return (
    <CustomerObservabilityContext.Provider value={value}>
      {children}
    </CustomerObservabilityContext.Provider>
  );
}

const NOOP_OBSERVABILITY: CustomerObservabilityContextValue = {
  setLastAuthProvider: () => undefined,
  setSalesPointCapabilityVersion: () => undefined,
  setActiveFlagsSnapshot: () => undefined,
};

export function useCustomerObservability(): CustomerObservabilityContextValue {
  const ctx = useContext(CustomerObservabilityContext);
  return ctx ?? NOOP_OBSERVABILITY;
}
